import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Mic, Library, Settings, Sparkles, TrendingUp, 
  Heart, Clock, ChevronRight, Play
} from 'lucide-react';
import { getVersionString } from '../../version';

interface QuickStat {
  label: string;
  value: string | number;
  icon: React.ReactNode;
}

interface RecentRecording {
  id: string;
  prompt: string;
  response: string;
  createdAt: string;
  rating?: number;
}

export const MobileHome: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [recentRecordings, setRecentRecordings] = useState<RecentRecording[]>([]);
  const [stats, setStats] = useState({
    totalRecordings: 0,
    thisWeek: 0,
    favorites: 0,
    streak: 0
  });

  useEffect(() => {
    loadStats();
    loadRecentRecordings();
  }, []);

  const loadStats = () => {
    const recordings = JSON.parse(localStorage.getItem('recordings') || '[]');
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    setStats({
      totalRecordings: recordings.length,
      thisWeek: recordings.filter((r: any) => new Date(r.createdAt) > weekAgo).length,
      favorites: recordings.filter((r: any) => r.isFavorite).length,
      streak: calculateStreak(recordings)
    });
  };

  const calculateStreak = (recordings: any[]): number => {
    // Simple streak calculation - consecutive days with recordings
    if (recordings.length === 0) return 0;
    
    const dates = recordings.map((r: any) => 
      new Date(r.createdAt).toDateString()
    );
    const uniqueDates = [...new Set(dates)].sort().reverse();
    
    let streak = 0;
    const today = new Date();
    for (let i = 0; i < uniqueDates.length; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(checkDate.getDate() - i);
      if (uniqueDates[i] === checkDate.toDateString()) {
        streak++;
      } else {
        break;
      }
    }
    return streak;
  };

  const loadRecentRecordings = () => {
    const recordings = JSON.parse(localStorage.getItem('recordings') || '[]');
    setRecentRecordings(recordings.slice(0, 3));
  };

  const quickStats: QuickStat[] = [
    { 
      label: 'Total', 
      value: stats.totalRecordings, 
      icon: <Library className="w-4 h-4" /> 
    },
    { 
      label: 'This Week', 
      value: stats.thisWeek, 
      icon: <Clock className="w-4 h-4" /> 
    },
    { 
      label: 'Favorites', 
      value: stats.favorites, 
      icon: <Heart className="w-4 h-4" /> 
    },
    { 
      label: 'Streak', 
      value: `${stats.streak}d`, 
      icon: <TrendingUp className="w-4 h-4" /> 
    }
  ];

  const formatTime = (date: string) => {
    const d = new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (hours < 48) return 'Yesterday';
    return d.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-slate-900 to-purple-900 text-white pb-20">
      {/* Header */}
      <div className="p-6 pb-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Selfi <span className="text-sm font-normal text-purple-400">{getVersionString()}</span></h1>
            <p className="text-gray-400 text-sm">Your inner voice, amplified</p>
            <div className="mt-2 flex items-center gap-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-xs text-gray-500">Private & Local - Only you can see this</span>
            </div>
          </div>
          <button
            onClick={() => navigate('/settings')}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <Settings className="w-6 h-6" />
          </button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          {quickStats.map((stat, index) => (
            <div
              key={index}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center"
            >
              <div className="flex justify-center mb-1 text-purple-400">
                {stat.icon}
              </div>
              <div className="text-lg font-semibold">{stat.value}</div>
              <div className="text-xs text-gray-400">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Actions */}
      <div className="px-6 mb-8">
        <button
          onClick={() => navigate('/record')}
          className="w-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl p-6 flex items-center justify-between hover:from-purple-600 hover:to-pink-600 transition-all transform hover:scale-[1.02] active:scale-[0.98]"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
              <Mic className="w-7 h-7" />
            </div>
            <div className="text-left">
              <h2 className="text-xl font-bold">Start Recording</h2>
              <p className="text-white/80 text-sm">Get your AI motivation now</p>
            </div>
          </div>
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {/* Recent Recordings */}
      <div className="px-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Recent Sessions</h2>
          <button
            onClick={() => navigate('/library')}
            className="text-purple-400 text-sm hover:text-purple-300 transition-colors"
          >
            View all
          </button>
        </div>

        {recentRecordings.length > 0 ? (
          <div className="space-y-3">
            {recentRecordings.map((recording) => (
              <button
                key={recording.id}
                onClick={() => navigate(`/library/${recording.id}`)}
                className="w-full bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:bg-white/10 transition-all text-left"
              >
                <div className="flex items-start justify-between mb-2">
                  <p className="text-sm text-gray-400 line-clamp-1 flex-1">
                    {recording.prompt}
                  </p>
                  <Play className="w-4 h-4 text-purple-400 ml-2" />
                </div>
                <p className="text-sm line-clamp-2 mb-2">
                  {recording.response}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {formatTime(recording.createdAt)}
                  </span>
                  {recording.rating && (
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <span
                          key={i}
                          className={`text-xs ${
                            i < recording.rating! ? 'text-yellow-500' : 'text-gray-600'
                          }`}
                        >
                          ★
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-8 text-center border border-white/10">
            <Sparkles className="w-12 h-12 mx-auto mb-3 text-purple-400" />
            <h3 className="font-semibold mb-2">No recordings yet</h3>
            <p className="text-sm text-gray-400 mb-4">
              Start your first session to get personalized motivation
            </p>
            <button
              onClick={() => navigate('/record')}
              className="px-6 py-2 bg-purple-500 rounded-lg text-sm font-medium hover:bg-purple-600 transition-colors"
            >
              Record Now
            </button>
          </div>
        )}
      </div>

      {/* Daily Inspiration */}
      <div className="px-6 mt-8">
        <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-4 border border-purple-500/30">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-purple-400 mt-0.5" />
            <div>
              <h3 className="font-semibold mb-1">Daily Inspiration</h3>
              <p className="text-sm text-gray-300">
                "The only impossible journey is the one you never begin. Today is your day to start."
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};